import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "../styles/AgendarCita.css";
import CardAnimada from "./CardAnimada";

import useDisponibilidad from "../hooks/useDisponibilidad";
import useDropdownData from "../hooks/useDropdownData";
import useProveedores from "../hooks/useProveedores";
import useActualizarStatusProveedor from "../hooks/useActualizarStatusProveedor";
import useObtenerCitaPorFecha from "../hooks/useObtenerCitaPorFecha";
import useActualizarCita from "../hooks/useActualizarCita";
import useEmail from "../hooks/useEmail";

import {
  construirFechaSinHoraExtra,
  calcularDuracionEnMinutos,
  formatearFecha,
  formatearHora,
  urlApi,
} from "../helpers/functionHelpers";
import { useAlert } from "../context/AlertContext";
import { emailActualizacionCita } from "../utils/email/emailActualizacionCita";

const EditarCita = () => {
  const { folio } = useParams();
  const navigate = useNavigate();

  const [dataCita, setDataCita] = useState({
    cab_id: "",
    fechaRsv: "",
    dataIni: "",
    dataFin: "",
    AlmacenAsignado_id: "",
    statuses_id: "",
    correoPrv: "",
    correoTrp: "",
    duracion: "",
    placa: "",
    personalAdcnl: "",
  });

  const [duracion, setDuracion] = useState("");
  const [horariosOcupados, setHorariosOcupados] = useState([]);
  const [horarioSeleccionado, setHorarioSeleccionado] = useState("");
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  const [datosPreGuardado, setDatosPreGuardado] = useState({});
  const [documentos, setDocumentos] = useState([]);

  const duraciones = [
    "15 minutos",
    "30 minutos",
    "45 minutos",
    "1 hora",
    "1.5 horas",
    "2 horas",
    "2.5 horas",
    "3 horas",
    "4 horas",
  ];

  const { disponibilidad, generarDisponibilidad } = useDisponibilidad(
    duracion,
    horariosOcupados
  );
  const {
    data: ubicacions,
    loading: loadingUbicacions,
    error: errorUbicacions,
  } = useDropdownData(`${urlApi}/api/ubicacions`,setDataCita,dataCita.AlmacenAsignado_id,true);
  const {
    data: statuses,
    loading: loadingStatus,
    error: errorStatus,
  } = useDropdownData(`${urlApi}/api/statuses`);
  const { actualizarCita, loading: loadingActualizarCita } =
    useActualizarCita();
  const { proveedores: cita } = useProveedores(
    `${urlApi}/api/citasFolio/${folio}`
  );
  const {
    citas,
    loading: loadingCitas,
    error: errorCitas,
  } = useObtenerCitaPorFecha(
    dataCita.AlmacenAsignado_id,
    construirFechaSinHoraExtra(dataCita.fechaRsv)
  );
  const { actualizarStatusProveedor } = useActualizarStatusProveedor();
  const { sendEmail } = useEmail();
  const { mostrarAlerta } = useAlert();

  const convertirAFechaLocal = (fechaUTC) => {
    const fecha = new Date(fechaUTC);
    //se debe hacer una conversión explicita con cada zona horaria requerida
    return new Date(fecha.getTime() + fecha.getTimezoneOffset() * 60000);
  };

  // Cargar la cita inicial al obtener los datos
  useEffect(() => {
    if (cita && cita.length > 0) {
      const citaDatos = cita[0];
      const horaInicio = formatearHora(citaDatos.dataIni);
      const horaFin = formatearHora(citaDatos.dataFin);
      const duracion = calcularDuracionEnMinutos(horaInicio, horaFin);
      setDataCita({
        cab_id: citaDatos.cab_id || "",
        fechaRsv: citaDatos.fechaRsv ? convertirAFechaLocal(citaDatos.fechaRsv) : null,
        dataIni: horaInicio || "",
        dataFin: horaFin || "",
        AlmacenAsignado_id: citaDatos.AlmacenAsignado_id || "",
        statuses_id: citaDatos.statuses_id || "",
        correoPrv: citaDatos.correoPrv || "",
        correoTrp: citaDatos.correoTrp || "",
        duracion: duracion,
        placa: citaDatos.placa || "",
        personalAdcnl: citaDatos.personalAdcnl || "",
      });
    }
  }, [cita]);

  // Cargar horarios ocupados cuando cambian el almacén o la fecha
  useEffect(() => {
    if (dataCita.AlmacenAsignado_id && dataCita.fechaRsv) {
      const horarios = citas.map((cita) => ({
        inicio: cita.dataIni,
        fin: cita.dataFin,
      }));
      setHorariosOcupados(horarios);
    }
  }, [dataCita.AlmacenAsignado_id, dataCita.fechaRsv, citas]);

  const fetchDocumentos = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${urlApi}/api/documentos/lista/${folio}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocumentos(data);
      }
    } catch (err) {
      console.error('Error al cargar documentos:', err);
    }
  };

  useEffect(() => {
    fetchDocumentos();
  }, [folio]);

  const obtenerDescripcion = (id, lista) => {
    const item = lista.find((element) => element.id === id);
    return item ? item.descripcion : "No disponible";
  };

  const obtenerName = (id, lista) => {
    const item = lista.find((element) => element.id === id);
    return item ? item.name : "No disponible";
  };

  const formatearDatosParaCard = (datos, ubicacions, statuses) => {
    // Obtener descripción amigable de los IDs
    const almacenDescripcion = obtenerDescripcion(
      datos.AlmacenAsignado_id,
      ubicacions
    );
    const estatusDescripcion = obtenerName(datos.statuses_id, statuses);

    // Calcular duración
    const duracion = calcularDuracionEnMinutos(datos.dataIni, datos.dataFin);

    return {
      Almacen: almacenDescripcion || "No especificado",
      Fecha: formatearFecha(datos.fechaRsv),
      "Hora de Inicio": datos.dataIni,
      "Hora de Fin": datos.dataFin,
      Duración: duracion,
      Estatus: estatusDescripcion || "No especificado",
    };
  };

  // Manejar cambios en los campos del formulario
  const handleChange = (e) => {
    const { name, value } = e.target;

    setDataCita((prevData) => ({
      ...prevData,
      [name]: ["AlmacenAsignado_id", "statuses_id"].includes(name)
        ? Number(value)
        : value,
    }));

    setMostrarConfirmacion(false); // Ocultar confirmación en caso de cambio
  };

  const handleDateChange = (date) => {
    setDataCita((prevData) => ({
      ...prevData,
      fechaRsv: date,
    }));
    setMostrarConfirmacion(false);
  };

  const handleDuracionChange = (e) => {
    const selectedDuration = e.target.value;
    setDuracion(selectedDuration);
    generarDisponibilidad(selectedDuration);
    setMostrarConfirmacion(false);
  };

  const handleHorarioSeleccionado = (slot) => {
    const [horaInicio, horaFin] = slot.split(" - ");
    setDataCita((prevData) => ({
      ...prevData,
      dataIni: horaInicio,
      dataFin: horaFin,
    }));
    setHorarioSeleccionado(slot);
    setMostrarConfirmacion(false);
  };

  const handlePreGuardarCita = () => {
    const { AlmacenAsignado_id, fechaRsv, dataIni, dataFin, statuses_id } =
      dataCita;

    if (fechaRsv) {
      const datosFormateados = formatearDatosParaCard(
        { AlmacenAsignado_id, fechaRsv, dataIni, dataFin, statuses_id },
        ubicacions,
        statuses
      );
      setDatosPreGuardado(datosFormateados); // Guarda los datos amigables
      setMostrarConfirmacion(true); // Muestra la card de confirmación
    } else {
      mostrarAlerta("Por favor ingrese una fecha.", "error");
    }
  };

  const handleConfirmarGuardar = async () => {
    const {
      AlmacenAsignado_id,
      cab_id,
      fechaRsv,
      dataIni,
      dataFin,
      statuses_id,
      correoTrp,
      correoPrv,
    } = dataCita;

    const almacenDescripcion = obtenerDescripcion(
      dataCita.AlmacenAsignado_id,
      ubicacions
    );

    const url = `${urlApi}/api/editarCita/${cab_id}`;
    const respuesta = await actualizarCita(
      AlmacenAsignado_id,
      fechaRsv,
      dataIni,
      dataFin,
      "",
      "",
      url
    );
    await actualizarStatusProveedor(folio, statuses_id);
    
    // Actualizar placas y personal
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${urlApi}/api/editarTransportista/${folio}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          placa: dataCita.placa,
          personalAdcnl: dataCita.personalAdcnl
        })
      });
    } catch (err) {
      console.error("Error al actualizar transportista:", err);
    }

    const message = emailActualizacionCita({
      folio,
      almacenDescripcion,
      fechaRsv: dataCita.fechaRsv,
      dataIni: dataCita.dataIni,
      dataFin: dataCita.dataFin,
    });

    const subject = `Actualización de la cita de almacenenes Sanfer con folio ${folio}`;

    const toEmail = [`${correoPrv}`, `${correoTrp}`];

    sendEmail({ to: toEmail, subject, html: message });

    if (respuesta) {
      mostrarAlerta("Cita actualizada correctamente.", "success");
      navigate(`/admin/calendario`);
    } else {
      mostrarAlerta("Hubo un problema al guardar la cita.", "error");
    }

    setMostrarConfirmacion(false);
  };

  const handleCancelarGuardar = () => {
    setMostrarConfirmacion(false);
  };

  return (
    <div className="agendar-cita-container">
      <div className="calendar-section">
        <Calendar
          onChange={handleDateChange}
          value={
            dataCita.fechaRsv instanceof Date ? dataCita.fechaRsv : new Date()
          }
        />
      </div>
      <div className="form-section">
        <div className="form-group">
          <label htmlFor="almacen">Almacén:</label>
          {loadingUbicacions ? (
            <p>Cargando ubicaciones...</p>
          ) : errorUbicacions ? (
            <p>Error al cargar ubicaciones: {errorUbicacions}</p>
          ) : (
            <select
              id="almacen"
              name="AlmacenAsignado_id"
              value={dataCita.AlmacenAsignado_id}
              onChange={handleChange}
            >
              <option value="">Seleccione</option>
              {ubicacions.map((ubicacion) => (
                <option key={ubicacion.id} value={ubicacion.id}>
                  {ubicacion.descripcion} {ubicacion.nombreEmpresa ? `(${ubicacion.nombreEmpresa})` : ''}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="duracion">Duración:</label>
          <select
            id="duracion"
            value={duracion}
            onChange={handleDuracionChange}
          >
            <option value="">Seleccione</option>
            {duraciones.map((duracion, index) => (
              <option key={index} value={duracion}>
                {duracion}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="Statuses">Estatus:</label>
          {loadingStatus ? (
            <p>Cargando estatus...</p>
          ) : errorStatus ? (
            <p>Error al cargar estatus: {errorStatus}</p>
          ) : (
            <select
              id="status"
              name="statuses_id"
              value={dataCita.statuses_id}
              onChange={handleChange}
            >
              <option value="">Seleccione</option>
              {statuses.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
          )}
        </div>
        
        <div className="form-group">
          <label htmlFor="placa">Placas del Vehículo:</label>
          <input
            type="text"
            id="placa"
            name="placa"
            value={dataCita.placa}
            onChange={handleChange}
            placeholder="Ej: ABC-1234"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="personalAdcnl">Personal Adicional:</label>
          <input
            type="number"
            id="personalAdcnl"
            name="personalAdcnl"
            value={dataCita.personalAdcnl}
            onChange={handleChange}
            min="0"
          />
        </div>

        <div className="form-group">
          <label htmlFor="disponibilidad">Disponibilidad:</label>
          <div className="horarios-disponibles">
            {loadingCitas ? (
              <p>Cargando horarios...</p>
            ) : errorCitas ? (
              <p>Error al cargar horarios: {errorCitas}</p>
            ) : disponibilidad.length > 0 ? (
              disponibilidad.map((slot, index) => (
                <motion.button
                  key={index}
                  className={`horario-slot ${
                    horariosOcupados.some(
                      (horario) =>
                        slot.inicio >= horario.inicio && slot.fin <= horario.fin
                    )
                      ? "ocupado"
                      : horarioSeleccionado === slot
                      ? "seleccionado"
                      : ""
                  }`}
                  onClick={() => handleHorarioSeleccionado(slot)}
                >
                  {slot}
                </motion.button>
              ))
            ) : (
              <p>No hay horarios disponibles.</p>
            )}
          </div>
        </div>
        
        <div className="form-group" style={{marginTop: '20px'}}>
          <label>Documentos de la Cita:</label>
          <div className="documentos-list" style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
            {documentos.length === 0 ? <p>No hay documentos cargados.</p> : (
              documentos.map(doc => (
                <div key={doc.id} style={{display: 'flex', justifyContent: 'space-between', padding: '10px', background: '#f5f5f5', borderRadius: '5px'}}>
                  <span>{doc.tipo_documento.toUpperCase()}: {doc.nombre_archivo}</span>
                  <a 
                    href={`${urlApi}/api/documentos/descargar/${folio}/${doc.tipo_documento}`} 
                    target="_blank" 
                    rel="noreferrer"
                    style={{color: '#0056b3', textDecoration: 'none'}}
                  >
                    Descargar
                  </a>
                </div>
              ))
            )}
          </div>
        </div>

        {mostrarConfirmacion ? (
          <CardAnimada
            data={datosPreGuardado}
            onConfirm={handleConfirmarGuardar}
            onCancel={handleCancelarGuardar}
          />
        ) : (
          <div className="form-section">
            <motion.button
              type="button"
              className="agendar-btn"
              onClick={handlePreGuardarCita}
              disabled={loadingActualizarCita}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 500, damping: 20 }}
            >
              {loadingActualizarCita ? "Guardando..." : "Editar cita"}
            </motion.button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditarCita;
